// Per primo: eventuale passaggio al dominio di un altro prodotto, prima di qualsiasi altro codice
import "./platform/earlyRedirect";
// Subito dopo: formato numeri italiano con migliaia anche a 4 cifre (solo app nativa), prima di ogni formattatore creato all'avvio
import "./lib/native/numberFormatBoot";
// Titolo e favicon del prodotto del dominio, prima che la pagina si disegni
import "./platform/head";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import App from "./App.tsx";
import { platform } from "./platform/runtime";
import { loadProductRoutes } from "./products/loader";
import "./index.css";
import "./styles/hig-mobile.css";
import { installToastHaptics } from "./lib/native/toastHaptics";
import { installTapEffects } from "./lib/native/tapEffects";
import { installKeyboard } from "./lib/native/keyboard";
import { installBackButton } from "./lib/native/backButton";

/** App nativa (iOS/Android): la webview si estende sotto la status bar/notch (look
 * integrato, non una fascia nera separata) — il padding va gestito via CSS safe-area
 * sui contenitori header (vedi ConstructionLayout). Testo/icone status bar scuri
 * perché l'header ha sfondo chiaro. */
// marcatore per gli stili «da app» (rimbalzo, stati di pressione): mai attivo sul web normale
try {
  if (Capacitor.isNativePlatform() || localStorage.getItem("ml_native_ux") === "1") {
    document.documentElement.classList.add("is-native");
    installTapEffects();
  }
} catch {
  /* storage non disponibile */
}

if (Capacitor.isNativePlatform()) {
  installToastHaptics();
  installKeyboard();
  installBackButton();
  StatusBar.setOverlaysWebView({ overlay: true }).catch(() => {});
  // Style.Light = testo/icone SCURI (per i nostri header chiari); Style.Dark = testo chiaro.
  StatusBar.setStyle({ style: Style.Light }).catch(() => {});
}

Sentry.init({
  // Solo collaudo e live: le prove in locale non devono finire nel progetto Sentry reale
  enabled: platform.env !== "locale",
  dsn: "https://9726944ede803a4b22dcb196fcb14e19@o4507181553025024.ingest.de.sentry.io/4511191500456016",
  environment: import.meta.env.MODE,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // App nativa: contenuti aziendali e foto non devono finire nei replay
      maskAllText: Capacitor.isNativePlatform(),
      blockAllMedia: Capacitor.isNativePlatform(),
    }),
  ],
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.05,
  replaysOnErrorSampleRate: 1.0,
});

Sentry.setTags({
  product: platform.product ?? "suite",
  host: window.location.hostname,
});

// Si caricano solo le route dei prodotti montati su questo host (un chunk per prodotto).
// Se un chunk non arriva (rete, deploy appena pubblicato) si riprova una volta ricaricando
// la pagina; se fallisce ancora si mostra un messaggio invece di una pagina bianca.
const RELOAD_FLAG = "ml_routes_reload";
loadProductRoutes(platform.mounted)
  .then((products) => {
    try {
      sessionStorage.removeItem(RELOAD_FLAG);
    } catch {
      /* storage non disponibile */
    }
    createRoot(document.getElementById("root")!).render(<App products={products} />);
  })
  .catch((error) => {
    Sentry.captureException(error);
    let alreadyRetried = false;
    try {
      alreadyRetried = sessionStorage.getItem(RELOAD_FLAG) === "1";
      if (!alreadyRetried) sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch {
      alreadyRetried = true;
    }
    if (!alreadyRetried) {
      window.location.reload();
      return;
    }
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML =
        '<div style="font-family:system-ui;padding:2rem;text-align:center">' +
        "<p>Impossibile caricare l'applicazione. Controlla la connessione e riprova.</p>" +
        '<button onclick="location.reload()" style="margin-top:1rem;padding:.5rem 1rem">Riprova</button></div>';
    }
  });
